import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px, motionMs } from '../theme';
import { layoutOf, makeTappable, watchResize } from '../ui/layout';
import { createScrollArea } from '../ui/scrollArea';
import { onTap } from '../ui/tap';

/**
 * Im Paket fuer itch.io gibt es keine Reichweitenmessung; die Datenschutzangabe
 * im Spiel sagt dann auch nichts davon, und die volle Seite blendet den
 * Widerspruch aus (`?itch`).
 */
const ITCH = import.meta.env.VITE_TARGET === 'itch';
import { createButton } from '../ui/button';
import { GAME_VERSION } from '../version';

/**
 * Impressum und Hinweise im Spiel.
 *
 * Die vollstaendige Anbieterkennzeichnung steht in `public/impressum.html` —
 * sie muss auch ohne laufendes JavaScript erreichbar sein. Diese Szene traegt
 * das, was Spielende tatsaechlich interessiert: dass das Spiel mit KI gebaut
 * wurde, dass nichts nach Hause telefoniert, und woher die Kartendaten kommen.
 */
export class ImprintScene extends Phaser.Scene {
  constructor() {
    super('Imprint');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    const layout = layoutOf(this);
    const { width, height, centreX } = layout;
    const columnWidth = Math.min(660, width - layout.margin * 2);
    const left = centreX - columnWidth / 2;
    const before = new Set(this.children.list);

    const title = this.add.text(0, 0, t('imprint.title'), titleStyle(22));
    const version = this.add.text(0, title.height + 8, t('imprint.version', { version: GAME_VERSION }), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    let y = version.y + version.height + 24;
    for (const section of ['ai', 'privacy', 'data'] as const) {
      y = this.renderSection(0, y, columnWidth, section);
    }

    // Verweis auf die vollstaendige Seite — antippbar, oeffnet einen neuen Tab.
    const link = this.add.text(0, y + 6, t('imprint.legal.hint'), {
      ...readoutStyle(11),
      color: TEXT_COLOUR.course,
      wordWrap: { width: columnWidth },
    });
    makeTappable(link);
    onTap(link, {
      onTap: () => window.open(ITCH ? './impressum.html?itch' : './impressum.html', '_blank', 'noopener'),
    });

    // Die Texte sind lang; auf einem Telefon reicht ein Bildschirm nicht.
    const created = this.children.list.filter((child) => !before.has(child));
    const scroll = createScrollArea(this, { x: left, y: 24, width: columnWidth, height: height - 24 - 84 });
    scroll.content.add(created);
    scroll.finish();

    const close = (): void => {
      this.scene.start('Menu');
    };
    createButton(this, centreX, height - 40, t('imprint.close'), close, { width: 180, fontSize: 12 });
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    });
    watchResize(this, () => this.scene.restart());

    this.cameras.main.fadeIn(motionMs(280), 5, 8, 13);
  }

  /** Zeichnet einen Abschnitt und liefert die Höhe darunter zurück. */
  private renderSection(
    left: number,
    top: number,
    columnWidth: number,
    key: 'ai' | 'privacy' | 'data',
  ): number {
    this.add.text(left, top, t(`imprint.${key}.heading`), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: TEXT_COLOUR.coast,
    });

    const bodyKey = key === 'privacy' && ITCH ? 'imprint.privacy.body.itch' : `imprint.${key}.body`;
    const body = this.add.text(left, top + 20, t(bodyKey), {
      ...readoutStyle(12),
      color: PALETTE.inkDim,
      wordWrap: { width: columnWidth },
      lineSpacing: 3,
    });

    return top + 20 + body.height + 26;
  }
}
